import React, { FunctionComponent, ReactNode, useState } from "react";

import { AlignItems, FlexDirection, FlexGrow, FlexWrap, Layout, Position } from "@multiweb/reactComponents";
import { bemClassNames } from "@multiweb/reactUtils";
import { Role } from "@multiweb/uiShared/Role";
import { ActionArea, DefaultRowTemplate, FrameSharedProps, RowTemplateType } from "../../types";
import { MeasureCell } from "../MeasureCell";
import { RowActions } from "../RowActions";

const THROTTLE_MILISECONDS = 200;
const ACTION_AREA_SIDE_SIZE = 0.25;

export type TemplateLayoutProps = {
    /** Obsah k zasazení do šablony. */
    contentItems: ReadonlyArray<ReactNode>;
    /** Ikony k zasazení do šablony. */
    iconItems: ReadonlyArray<ReactNode>;
    /** Určuje, jakým způsobem se bude zobrazovat obsah (SingleLine, Multiline, atd). */
    rowTemplate?: RowTemplateType;
    /** Oblast, do které se mají akce vykreslit. */
    actionArea?: ActionArea;
    /** Velikostní poměr (procentní, zadaný mezi 0 a 1) části ActionArea vzhledem k celému řádku */
    actionAreaSize?: number;
    /** Set to true to override default flex-shrink: 0 actions area*/
    actionAreaShrink?: boolean;
} & FrameSharedProps;

const classNames = bemClassNames("template");

/**
 * Komponenta sloužící k vykreslení atributů.
 */
export const TemplateLayout: FunctionComponent<TemplateLayoutProps> = ({
    dataSourceKey,
    recordId,
    contentItems,
    iconItems,
    rowTemplate = DefaultRowTemplate.singleLine,
    actionArea = ActionArea.aaRight,
    actionAreaSize = ACTION_AREA_SIDE_SIZE,
    actionAreaShrink = false,
}) => {
    const [width, setWidth] = useState<number | undefined>(undefined);

    return (
        <Layout flexGrow={FlexGrow.one} flexWrap={FlexWrap.nowrap}>
            <Layout alignItems={AlignItems.center}>{iconItems}</Layout>
            <Layout
                flexWrap={FlexWrap.nowrap}
                flexDirection={actionArea === ActionArea.aaBottom ? FlexDirection.column : FlexDirection.row}
                flexGrow={FlexGrow.one}
                alignItems={AlignItems.stretch}
                position={Position.relative}
            >
                <MeasureCell onWidthChange={setWidth} wait={THROTTLE_MILISECONDS} />
                <div className={`${classNames("content")} ${rowTemplate}`}>{contentItems}</div>
                <div
                    className={classNames("actions", { [actionArea]: true, aaShrink: actionAreaShrink })}
                    role={Role.cell}
                >
                    <RowActions {...{ dataSourceKey, recordId }} maxWidth={(width || 0) * actionAreaSize} />
                </div>
            </Layout>
        </Layout>
    );
};
